import { StyledDatePicker, StyledTextfield } from 'pages/InfoRequestPage.styles'
import { forwardRef, LegacyRef, useRef, useState, useEffect } from 'react'
import { FormState } from 'typings'
import 'react-datepicker/dist/react-datepicker.min.css'
import { formatDate, placeholderFunction, formatTextDateInput } from 'helpers'
import {
  isValidDateString,
  convertStringToDate
} from 'helpers/fieldValidations'
import { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import { currentLanguage } from 'helpers/language'
import errorMessages from 'const/errorMessages'
import useCustomTranslation from 'hooks/useCustomTranslation'
import Icon from './Icon'

registerLocale('es', es)

export interface CustomInputProps {
  formValues: FormState
  /** Weird reason, onChange prop name trigger an error */
  onDate: (date: Date) => void
  onClick: () => void
  testId?: string
}

export interface CalendarProps {
  formValues: FormState
  handleOnChange: (value: Date) => void
  handleOnBlur: (target: string) => void
  testId?: string
}

const CustomInput = forwardRef(
  (
    { formValues, onDate, onClick, testId = '' }: CustomInputProps,
    ref: LegacyRef<HTMLDivElement>
  ) => {
    const t = useCustomTranslation('form.personal')
    const [date, setDate] = useState('')
    // local error has preference over parent error
    const [localError, setLocalError] = useState('')

    useEffect(() => {
      if (formValues.dateOfBirth) setDate(formatDate(formValues.dateOfBirth))
    }, [formValues.dateOfBirth])

    useEffect((): void => {
      if (date) {
        const isValidFormat = isValidDateString(date)
        setLocalError(isValidFormat ? '' : errorMessages.invalidBirthDate)
      }
    }, [date])

    return (
      <div ref={ref}>
        <StyledTextfield
          type="text"
          id="dateOfBirth"
          name="dateOfBirth"
          placeholder={t('dateOfBirth')}
          matchPattern={/^(\d|\/)*$/}
          onChange={(e: React.SyntheticEvent<HTMLInputElement, Event>) => {
            const target = e.target as HTMLInputElement
            const value = formatTextDateInput(target.value)
            setDate(value)
            // if value is valid date we proceed
            if (isValidDateString(value)) {
              const newDate = convertStringToDate(value)
              onDate(newDate)
            }
          }}
          onBlur={() => {
            // clean up state if date is not valid, but keep text
            if (!isValidDateString(date)) {
              onDate(undefined as unknown as Date)
            }
          }}
          onFocus={onClick}
          value={date}
          error={localError || formValues.errors.dateOfBirth}
          autoComplete="bday"
          errorIcon={Icon('error')}
          maxLength={10}
          data-testid={testId}
        />
      </div>
    )
  }
)

const Calendar = ({
  formValues,
  handleOnChange,
  handleOnBlur,
  testId = ''
}: CalendarProps): JSX.Element => {
  const inputRef = useRef(null)

  return (
    <StyledDatePicker
      selected={formValues.dateOfBirth}
      openToDate={formValues.dateOfBirth ?? undefined}
      maxDate={new Date()}
      onChange={handleOnChange}
      onBlur={() => handleOnBlur('dateOfBirth')}
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      locale={currentLanguage || ''}
      customInput={
        <CustomInput
          formValues={formValues}
          onClick={placeholderFunction}
          onDate={handleOnChange}
          ref={inputRef}
          testId={testId}
        />
      }
    />
  )
}

export default Calendar
